/**
 * Header Component
 *
 * Desktop (>md): logo left, nav links + icons right.
 * Mobile/Tablet (<=md): hamburger left, logo center, cart right.
 *   Nav links move into a Drawer that slides from the left with
 *   focus-trap, search shortcut, and full ARIA support.
 */

'use client';

import { Drawer, Badge, type LogoCustomizationAsset } from '@template-project/afrodita-components';
import { User, Menu, X, Search, ShoppingBag } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCartContext } from '@/lib/context/cart-context';
import { useCart } from '@/lib/hooks/use-cart';
import { authService } from '@/lib/services/auth.service';
import { Tooltip } from '@/components/Tooltip/Tooltip';
import { CountrySelector } from '@/components/CountrySelector/CountrySelector';
import { useIntl } from '@/lib/intl';
import { useTranslation } from '@/lib/i18n';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavCollections, useSaleCollection } from '@/lib/hooks/use-nav-collections';
import {
  HEADER_ACCESSIBILITY_CLOSE_MENU,
  HEADER_ACCESSIBILITY_HOME,
  HEADER_ACCESSIBILITY_NAV_MENU,
  HEADER_ACCESSIBILITY_OPEN_CART,
  HEADER_ACCESSIBILITY_OPEN_MENU,
  HEADER_LABEL_MY_ACCOUNT,
  HEADER_LABEL_MY_ACCOUNT_DRAWER,
  HEADER_LABEL_SEARCH_PRODUCTS,
  HEADER_PLACEHOLDER_SEARCH_PRODUCTS,
} from './i18.keys';
import {
  HeaderContainer,
  Logo,
  HeaderBrandLogo,
  DesktopNav,
  NavLink,
  SaleLink,
  MobileLeft,
  MobileRight,
  HeaderIconButton,
  DrawerCloseRow,
  SearchShortcut,
  DrawerNav,
  DrawerNavLink,
  DrawerSaleLink,
  DrawerSection,
  DrawerAccountButton,
  DrawerScrollArea,
  MobileDrawerFocusTrap,
  DrawerLayout,
} from './Header.styled';


const SALE_COLLECTION_SLUG = 'summer-sale';
const MAX_NAV_LINKS = 7;
const MOBILE_MENU_ID = 'mobile-nav-drawer';

/* ------------------------------------------------------------------ */
/*  Header Component                                                   */
/* ------------------------------------------------------------------ */

interface HeaderProps {
  siteTitle?: string;
  logo?: LogoCustomizationAsset;
}

export function Header({ siteTitle = 'Shapermint', logo }: Readonly<HeaderProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const { localizedPath } = useIntl();
  const { t } = useTranslation();
  const { showCart, cartToken, clearCart } = useCartContext();
  const { data: cart } = useCart(cartToken, clearCart);
  const { collections: navCollections } = useNavCollections();
  const saleData = useSaleCollection();
  const saleLabel = saleData?.title ?? 'Summer Sale';

  const [displayName, setDisplayName] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const hamburgerRef = useRef<HTMLButtonElement>(null);

  const itemCount = cart?.items.reduce((sum, item) => sum + item.quantity, 0) || 0;

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    const checkSession = async () => {
      const storedDisplayName = authService.getDisplayName();
      setDisplayName(storedDisplayName);

      if (authService.isAuthenticated()) {
        setIsLoggedIn(true);
      } else if (authService.hasSession()) {
        const result = await authService.tryRestoreSession();
        setIsLoggedIn(result.success);
        if (!result.success) setDisplayName(null);
      } else {
        setIsLoggedIn(false);
      }
    };
    checkSession();
  }, [location.pathname]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleUserIconClick = () => {
    if (isLoggedIn) {
      navigate(localizedPath('/my-account'));
    } else {
      navigate(localizedPath('/login'));
    }
  };

  const handleSearchClick = () => {
    closeMenu();
    navigate(localizedPath('/search'));
  };

  const userIcon = (
    <HeaderIconButton
      icon={<User size={24} aria-hidden />}
      onClick={handleUserIconClick}
      aria-label={t(HEADER_LABEL_MY_ACCOUNT)}
    />
  );

  const cartButton = (
    <HeaderIconButton
      onClick={showCart}
      aria-label={t(HEADER_ACCESSIBILITY_OPEN_CART)}
      icon={
        <>
          <ShoppingBag size={24} aria-hidden />
          {itemCount > 0 && (
            <Badge
              intent="urgent"
              shape="circle"
              size="xs"
              uppercase={false}
              style={{ position: 'absolute', top: 0, right: 0 }}
            >
              {itemCount}
            </Badge>
          )}
        </>
      }
    />
  );

  return (
    <>
      <HeaderContainer>
        <MobileLeft>
          <HeaderIconButton
            ref={hamburgerRef}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? t(HEADER_ACCESSIBILITY_CLOSE_MENU) : t(HEADER_ACCESSIBILITY_OPEN_MENU)}
            aria-expanded={menuOpen}
            aria-controls={MOBILE_MENU_ID}
            icon={menuOpen ? <X size={24} aria-hidden /> : <Menu size={24} aria-hidden />}
          />
        </MobileLeft>

        <Logo to={localizedPath('/')} aria-label={t(HEADER_ACCESSIBILITY_HOME)}>
          <HeaderBrandLogo logo={logo} fallbackText={siteTitle} decorative />
        </Logo>

        <DesktopNav>
          {navCollections.slice(0, MAX_NAV_LINKS).map(({ label, slug }) => (
            <NavLink key={slug} to={localizedPath(`/collections/${slug}`)}>
              {label}
            </NavLink>
          ))}

          <SaleLink to={localizedPath(`/collections/${SALE_COLLECTION_SLUG}`)}>
            {saleLabel}
          </SaleLink>

          <HeaderIconButton
            onClick={handleSearchClick}
            aria-label={t(HEADER_LABEL_SEARCH_PRODUCTS)}
            icon={<Search size={24} aria-hidden />}
          />
          <CountrySelector />
          {isLoggedIn && displayName ? (
            <Tooltip content={displayName} position="bottom">
              {userIcon}
            </Tooltip>
          ) : (
            userIcon
          )}
          {cartButton}
        </DesktopNav>

        <MobileRight>
          <HeaderIconButton
            onClick={handleSearchClick}
            aria-label={t(HEADER_LABEL_SEARCH_PRODUCTS)}
            icon={<Search size={24} aria-hidden />}
          />
          {cartButton}
        </MobileRight>
      </HeaderContainer>

      <Drawer
        isOpen={menuOpen}
        onClose={closeMenu}
        position="left"
        width="80%"
      >
        <MobileDrawerFocusTrap
          isOpen={menuOpen}
          onClose={closeMenu}
          triggerRef={hamburgerRef}
        >
          <DrawerLayout>
            <dialog id={MOBILE_MENU_ID} open aria-modal="true" aria-label={t(HEADER_ACCESSIBILITY_NAV_MENU)} style={{ display: 'contents', padding: 0, border: 'none', background: 'none' }}>
              <DrawerCloseRow>
                <HeaderIconButton
                  onClick={closeMenu}
                  aria-label={t(HEADER_ACCESSIBILITY_CLOSE_MENU)}
                  icon={<X size={24} aria-hidden />}
                />
              </DrawerCloseRow>

              <DrawerScrollArea>
                <SearchShortcut onClick={handleSearchClick} aria-label={t(HEADER_LABEL_SEARCH_PRODUCTS)}>
                  <Search size={24} aria-hidden />
                  {t(HEADER_PLACEHOLDER_SEARCH_PRODUCTS)}
                </SearchShortcut>

                <DrawerNav>
                  {navCollections.slice(0, MAX_NAV_LINKS).map(({ label, slug }) => (
                    <DrawerNavLink key={slug} to={localizedPath(`/collections/${slug}`)} onClick={closeMenu}>
                      {label}
                    </DrawerNavLink>
                  ))}
                  <DrawerSaleLink to={localizedPath(`/collections/${SALE_COLLECTION_SLUG}`)} onClick={closeMenu}>
                    {saleLabel}
                  </DrawerSaleLink>
                </DrawerNav>

                <DrawerSection>
                  <DrawerAccountButton
                    variant="ghost"
                    leftIcon={<User size={24} aria-hidden />}
                    onClick={() => { handleUserIconClick(); closeMenu(); }}
                    aria-label={t(HEADER_LABEL_MY_ACCOUNT)}
                  >
                    {isLoggedIn && displayName ? displayName : t(HEADER_LABEL_MY_ACCOUNT_DRAWER)}
                  </DrawerAccountButton>

                  <CountrySelector variant="drawer" />
                </DrawerSection>
              </DrawerScrollArea>
            </dialog>
          </DrawerLayout>
        </MobileDrawerFocusTrap>
      </Drawer>
    </>
  );
}
